import { type ReactNode, useEffect, useMemo, useRef } from 'react';

/**
 * **The whole of `legacy/frontend/src/screens/TimeWheel.tsx`, but this comment.**
 *
 * It arrived in three slices, each bringing what its first consumer needed, and none of them
 * re-implemented anything smaller. F-005 brought the panel a wheel stands in, `WheelBox`, and
 * `MONTHS_AHEAD`, because «Когда» imports `WheelBox` (`WhenField.tsx:15`) and brings its own
 * three columns. F-014 brought `TimeWheel` itself (`:29-88`), the hour-and-minute wheel an
 * arrival time is picked on, because `AttendanceRow.tsx:7` imports it, and with it `report`,
 * `useStanding`, `HOURS`, `minuteRows`, `clockParts`, `pad` and `clamp`. F-026 brought the rest:
 * `DateWheel` (`:90-127`), the one column of real dates a `once` sending is picked on, `dayRows`
 * (`:168-182`), and the one import only they read, `formatWheelDate` (`:3`) — each into the hole
 * it stood in, so every line below this comment is the old file's, in its order.
 *
 * `MONTHS_AHEAD` is unexported again, as it is there (`:7`). While `dayRows` was absent the
 * constant carried an `export` so that `noUnusedLocals` would accept it; its reader is back, and
 * `Wheel.test.tsx` holds the 18 months through `DateWheel`'s last row instead. «Когда» keeps its
 * own second copy of the same 18 (`WhenField.tsx:90`), as it does there. `Column` is ported
 * whole and separately, as `./Wheel`, which is where the old app keeps it too.
 */

import { formatWheelDate } from '../format';
import { Column } from './Wheel';

/** How far ahead a date column runs. Past that nobody is scheduling anything. */
const MONTHS_AHEAD = 18;

/**
 * The panel a wheel stands in: the band, the columns, and the two gradients.
 *
 * It was written out by hand in `WhenField` while the schedule's date was the
 * only wheel with more than one column. Three more places needed the same five
 * elements in the same order — and a band that is a pixel off in one of them is
 * a wheel that marks the wrong row — so the shell is one component and the
 * columns are what the caller brings.
 */
export function WheelBox({ modifier, children }: { modifier?: string; children: ReactNode }) {
  return (
    <div className={modifier === undefined ? 'wheel' : `wheel ${modifier}`}>
      <span className="wheel-band" aria-hidden="true" />
      <div className="wheel-cols">{children}</div>
      <span className="wheel-fade wheel-fade--top" aria-hidden="true" />
      <span className="wheel-fade wheel-fade--bottom" aria-hidden="true" />
    </div>
  );
}

/**
 * An hour and a minute, on the wheel the rest of the app already picks with.
 *
 * `null` is a time nobody has named yet, and it is not the same as a time that
 * happens to be nine in the morning: the button under both of these wheels is
 * dead until a value is reported, and a field that reported one by being drawn
 * would let a regent save a minute nobody ever said out loud. So an empty wheel
 * stands on `fallback` and keeps quiet — the first flick, or a tap on a
 * neighbouring row, is what speaks.
 */
export function TimeWheel({
  label,
  value,
  fallback,
  step,
  onChange,
}: {
  /** What the columns are called to a screen reader: «Во сколько: час». */
  label: string;
  /** «HH:mm», or null for a time nobody has chosen. */
  value: string | null;
  /** «HH:mm» the wheel stands on until one is chosen. */
  fallback: string;
  /**
   * How far apart the minutes are. Five for a sending — nobody schedules one
   * for 19:23 — and one for an arrival, because lateness is counted off it and
   * rounding seven minutes to five would make the number untrue.
   */
  step: 1 | 5;
  onChange: (value: string) => void;
}) {
  const minutes = useMemo(() => minuteRows(step), [step]);
  const [hour, minute] = clockParts(value ?? fallback);
  const minuteIndex = clamp(Math.round(minute / step), minutes.length);
  const shown = `${pad(hour)}:${pad(minuteIndex * step)}`;
  useStanding(value, shown, onChange);

  function set(nextHour: number, nextMinute: number): void {
    report(`${pad(nextHour)}:${pad(nextMinute)}`, shown, onChange);
  }

  return (
    <WheelBox>
      <Column
        label={`${label}: час`}
        items={HOURS}
        index={hour}
        onPick={(index) => set(index, minuteIndex * step)}
        mono
      />
      <Column
        label={`${label}: минуты`}
        items={minutes}
        index={minuteIndex}
        onPick={(index) => set(hour, index * step)}
        mono
      />
    </WheelBox>
  );
}

/**
 * One column of real dates, from today forward.
 *
 * Real ones and not a number beside a month, for the reason «Когда» gives: 31
 * February and last year are not in the column at all, so the server's refusal
 * stays a guard rather than something anybody meets. The column starts today
 * because everything asked for on it is a thing that has yet to happen.
 */
export function DateWheel({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string;
  /** «YYYY-MM-DD», or null for a date nobody has chosen. */
  value: string | null;
  fallback: string;
  onChange: (value: string) => void;
}) {
  const days = useMemo(dayRows, []);
  // A date the column does not hold - one already past - stands on the first
  // row it does hold, which is today.
  const index = clamp(days.values.indexOf(value ?? fallback), days.values.length);
  const shown = days.values[index];
  useStanding(value, shown, onChange);

  return (
    <WheelBox>
      <Column
        label={label}
        items={days.labels}
        index={index}
        onPick={(row) => report(days.values[row], shown, onChange)}
      />
    </WheelBox>
  );
}

/**
 * A value the wheel settled on, reported unless it is the one already showing.
 *
 * The wheel reports itself again after every scroll it is sent on, its own
 * arrival included, and being put where it already stands is not a choice
 * anybody made. This is also the whole of the empty rule: with nothing chosen
 * the wheel stands on `fallback`, so the scroll that puts it there reports
 * `fallback` and is swallowed here.
 */
function report(next: string, shown: string, onChange: (value: string) => void): void {
  if (next === shown) return;
  onChange(next);
}

/**
 * The one case in which a wheel speaks without being touched: a value it cannot
 * draw.
 *
 * A time typed into the field this replaces could land between the rows — 09:23
 * on a wheel that goes in fives — and a `once` sending written last month has a
 * date the column no longer starts early enough to hold. The wheel then shows
 * one thing while the draft holds another, and «Сохранить» sends the one nobody
 * is looking at. Saying where it stands costs a value that was already there;
 * it never happens to an empty wheel, which has nothing to disagree with.
 */
function useStanding(value: string | null, shown: string, onChange: (value: string) => void): void {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (value !== null && value !== shown) onChangeRef.current(shown);
  }, [value, shown]);
}

const HOURS = Array.from({ length: 24 }, (_, hour) => pad(hour));

function minuteRows(step: number): string[] {
  return Array.from({ length: 60 / step }, (_, row) => pad(row * step));
}

/** Every day from today to eighteen months on: the value and what it reads as. */
function dayRows(): { values: string[]; labels: string[] } {
  const now = new Date();
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const last = new Date(day);
  last.setMonth(last.getMonth() + MONTHS_AHEAD);
  const values: string[] = [];
  const labels: string[] = [];
  while (day.getTime() <= last.getTime()) {
    values.push(`${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`);
    labels.push(formatWheelDate(day));
    day.setDate(day.getDate() + 1);
  }
  return { values, labels };
}

function clockParts(clock: string): [number, number] {
  const said = /^(\d{1,2}):(\d{2})/.exec(clock);
  if (said === null) return [0, 0];
  return [clamp(Number(said[1]), 24), clamp(Number(said[2]), 60)];
}

function pad(figure: number): string {
  return String(figure).padStart(2, '0');
}

function clamp(index: number, length: number): number {
  return Math.max(0, Math.min(length - 1, index));
}
